// This file is for any functions that are shared between server and front-end.
// TRPC will not allow (eg) importing a zod schema from the router it's used in.
// If it is defined here then both server and browser can access it.

import { z } from "zod";

import {
  BooleanMode,
  SavedSearchSubscriptionFrequency,
  SavedSearchSubscriptionIncludedResultTypes,
} from "@prisma-postgres";
import { DateTime } from "luxon";

export const MAX_GROUP_DEPTH = 4;

const zBlockBase = z.object({
  exclude: z.boolean(),
});

export type TBlockBase = z.infer<typeof zBlockBase>;

const zSavedSearchBlock = zBlockBase.extend({
  savedSearchId: z.string(),
});

export type TSavedSearchBlock = z.infer<typeof zSavedSearchBlock>;

const zTextBlock = zBlockBase.extend({
  value: z.string(),
});

export type TTextBlock = z.infer<typeof zTextBlock>;

export interface IGroupBlock extends TBlockBase {
  booleanMode: BooleanMode;
  savedSearchBlocks: TSavedSearchBlock[];
  textBlocks: TTextBlock[];
  startsWithBlocks: TTextBlock[];
  groupBlocks: IGroupBlock[];
  usernameBlocks: TTextBlock[];
}

export const zGroupBlock: z.ZodSchema<IGroupBlock> = z.lazy(() =>
  zBlockBase.extend({
    booleanMode: z.nativeEnum(BooleanMode),
    savedSearchBlocks: z.array(zSavedSearchBlock),
    textBlocks: z.array(zTextBlock),
    startsWithBlocks: z.array(zTextBlock),
    usernameBlocks: z.array(zTextBlock),
    groupBlocks: z.array(zGroupBlock),
  }),
);

export const BLOCKED_IMAGE_URL = "/images/blocked-image.png";

export const MAX_NARRATIVES_PER_SEARCH = 3;

export const zNarrative = z
  .string()
  .trim()
  .refine((val) => val.split(" ").length >= 3, {
    message: "Narrative must contain at least 3 words",
  })
  .refine((val) => val.split(" ").length <= 25, {
    message: "Narrative cannot exceed 25 words",
  });

const zPlatformBase = z.object({ id: z.string().uuid(), name: z.string() });
const zPlatform = zPlatformBase.extend({ isCurrentlyCollected: z.boolean() });

export const zPlatformPackageSetting = z.union([
  zPlatformBase.extend({
    isAvailable: z.literal(true),
    isSelected: z.literal(true),
    platforms: z.array(
      zPlatform.extend({
        isAvailable: z.literal(true),
        isSelected: z.literal(false),
      }),
    ),
  }),
  zPlatformBase.extend({
    isAvailable: z.literal(true),
    isSelected: z.literal(false),
    platforms: z.array(
      zPlatform.extend({
        isAvailable: z.literal(true),
        isSelected: z.boolean(),
      }),
    ),
  }),
  zPlatformBase.extend({
    isAvailable: z.literal(false),
    isSelected: z.literal(false),
    platforms: z.array(
      z.union([
        zPlatform.extend({
          isAvailable: z.literal(true),
          isSelected: z.boolean(),
        }),
        zPlatform.extend({
          isAvailable: z.literal(false),
          isSelected: z.literal(false),
        }),
      ]),
    ),
  }),
]);

export type TPlatformPackageSetting = z.infer<typeof zPlatformPackageSetting>;

export type TFilterSettings = z.infer<typeof zFilterSettings>;

/**  The pattern here should be
 * `{kind: {include: {...}, exclude: {...}}}`
 * Each `kind` [eg `platform`, `username`] may support one or both of `include` and `exclude` depending on what the UI functionality allows for.
 */
export const zFilterSettings = z.object({
  id: z.string().uuid().optional(),
  platforms: z.object({
    include: z.array(zPlatformPackageSetting),
  }),
});

export const zCreateSavedSearchInput = z.object({
  name: z.string().min(1),
  groupBlock: zGroupBlock,
  filterSettings: zFilterSettings,
});

export const zUpdateSavedSearchInput = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  groupBlock: zGroupBlock,
  filterSettings: zFilterSettings,
});

export const zUpdateStructureInput = z.object({
  id: z.string().uuid(),
  groupBlock: zGroupBlock,
});

export const zSubscriptionFrequency = z.nativeEnum(
  SavedSearchSubscriptionFrequency,
);

export const zSubscriptionIncludeResults = z.nativeEnum(
  SavedSearchSubscriptionIncludedResultTypes,
);

export const zSharedPost = z.object({
  id: z.string(),
  username: z.string().nullable(),
  timestamp: z.string().datetime(),
  platform: z.string(),
  url: z.string().nullable(),
  text: z.string(),
  media: z.string(),
});

export type PostParsed = z.infer<typeof zPostParsed>;

export const zPostParsed = z.object({
  id: z.string(),
  username: z.string(),
  user_id: z.string().nullable(), // Some platforms don't provide a user_id
  text: z.string(),
  timestamp: z.string(),
  location: z.string().nullable().optional(),
  platform: z.string(),
  negative: z.number(),
  offensive: z.number(),
  hateful: z.number(),
  violent: z.number(),
  conversation: z.string().nullable(),
  raw_relevance: z.number().optional(),
  url: z.string().nullable(),
  media: z.string(),
});

export type UserData = z.infer<typeof zUserData>;

export const zUserData = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  avatar: z.string(),
  btc_address: z.string(),
  created_date: z.bigint(),
  description: z.string(),
  eth_wallet: z.string(),
  followers_count: z.number().nullable(),
  following_count: z.number().nullable(),
  statuses_count: z.number().nullable(),
  location: z.string(),
  website: z.string(),
  url: z.string(),
  platform: z.string(),
  social_profiles: z.string(),
});

export const EXPLORER_TIME_RANGES = [
  "Last 2 days",
  "Last 7 days",
  "Last 14 days",
  "Last 30 days",
  "Last 60 days",
  "Last 90 days",
  "Last 180 days",
  "Custom",
] as const;

export type TTimeRanges = (typeof EXPLORER_TIME_RANGES)[number];

export type DateRange = [DateTime | null, DateTime | null];

export const zRunSavedSearchInput = z.object({
  savedSearchId: z.string(),
  timeRange: z.enum(EXPLORER_TIME_RANGES),
  customRange: z.array(z.string().nullable(), z.string().nullable()).optional(),
});

// Note: Does not validate array length! This way the UI doesn't crash when you've only entered one date.
export const zCustomDateRange = z.string().transform((s) =>
  s
    .split(",")
    .map((d) => (d === "" ? null : DateTime.fromISO(d)))
    .filter((d) => d === null || d.isValid),
);

const emptyStringToNull = (value: string) => (value.length ? value : null);

export const DisplayNameForSubscriptionIncludedResultType = {
  [SavedSearchSubscriptionIncludedResultTypes.All]: "all",
  [SavedSearchSubscriptionIncludedResultTypes.HighlyHateful]: "highly hateful",
  [SavedSearchSubscriptionIncludedResultTypes.HighlyOffensive]:
    "highly offensive",
  [SavedSearchSubscriptionIncludedResultTypes.HighlyViolent]: "highly violent",
} as const;

export const DisplayNameForSubscriptionFrequency = {
  [SavedSearchSubscriptionFrequency.Daily]: "daily",
  [SavedSearchSubscriptionFrequency.EveryThreeHours]: "every three hours",
  [SavedSearchSubscriptionFrequency.Immediately]: "immediately",
  [SavedSearchSubscriptionFrequency.Monthly]: "monthly",
  [SavedSearchSubscriptionFrequency.TwicePerDay]: "twice per day",
  [SavedSearchSubscriptionFrequency.TwicePerWeek]: "twice per week",
  [SavedSearchSubscriptionFrequency.Weekly]: "weekly",
};

export type TExplorerHighlightText = {
  type: "exact text" | "starts with" | "user keyword filter";
  text?: string;
  texts?: string[]; // This is necessary to enable highlighting of multiple keywordFilter values at the same time
};

// zPlatformUsersFormatted and zSearchResult are unneccesary and should be addressed with more consistency throughout
export const zPlatformUsersFormatted = z.object({
  avatar: z.string().transform(emptyStringToNull).nullable(),
  btc_address: z.string().transform(emptyStringToNull).nullable().optional(),
  created_date: z.string().datetime().nullable(),
  description: z.string().nullable(),
  eth_wallet: z.string().nullable().optional(),
  followers_count: z.number().nullable(),
  following_count: z.number().nullable(),
  id: z.string(),
  location: z.string().nullable(),
  name: z.string(),
  platform: z.string(),
  social_profiles: z.string(),
  statuses_count: z.number().nullable(),
  url: z.string().url().nullable(),
  username: z.string(),
  website: z.string().nullable(),
});

export const getPlatformIdsForSelected = (
  filterPlatforms: TFilterSettings["platforms"]["include"],
  onlySelected: boolean,
) => {
  return {
    platformPackageIds: filterPlatforms
      .filter((platformPackage) =>
        onlySelected ? platformPackage.isSelected : true,
      )
      .map((platformPackage) => platformPackage.id),
    platformIds: filterPlatforms.flatMap((platformPackage) =>
      platformPackage.platforms
        .filter((platform) => (onlySelected ? platform.isSelected : true))
        .map((platform) => platform.id),
    ),
  };
};

export const getS3ImageURL = (bucketName: string, key: string) => {
  return `https://${bucketName}.s3.amazonaws.com/${key}`;
};
